import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { AppParameters } from '@itero/standard-launch-sdk';
import { KnownComponentsNames } from '@core/components-names';

export interface OverlayState {
	isOpen: boolean;
	appParameters?: AppParameters;
	returnUrl?: string;
}
// This service could be removed after TR-73441 and when all places will be utilized by the epic functionality.
@Injectable({
	providedIn: 'root'
})
export class ImageHubOverlayService {
	private readonly IosimPlusRoute = '/doctors/iosim-plus/';
	private readonly ViewerRoute = '/viewer-wrapper/';
	private readonly VIEWER_STORAGE_KEY = 'web-viewer-image-hub-url';
	private readonly STORAGE_KEY = 'overlayState';
	private readonly overlayStateSubject = new BehaviorSubject<OverlayState>(
		JSON.parse(sessionStorage.getItem(this.STORAGE_KEY) ?? 'null') ?? {
			isOpen: false,
			appParameters: undefined,
			returnUrl: undefined
		}
	);

	public overlayState$: Observable<OverlayState> = this.overlayStateSubject.asObservable();

	constructor(private readonly router: Router) {}

	public shouldShowAsOverlay(targetAppId: string): boolean {
		const isImageHub = targetAppId === KnownComponentsNames.ImageHub;
		const isFromIosimPlus = this.router.url.includes(this.IosimPlusRoute);
		const isFromViewer = this.router.url.includes(this.ViewerRoute);
		return isImageHub && (isFromIosimPlus || isFromViewer);
	}

	public openOverlay(appParameters: AppParameters): string {
		const returnUrl = this.router.url;
		const processedAppParameters = this.processAppParameters(appParameters);

		this.setOverlayState({
			isOpen: true,
			appParameters: processedAppParameters,
			returnUrl
		});

		// Store URL in sessionStorage for viewer route
		if (this.router.url.includes(this.ViewerRoute)) {
			this.storeViewerUrl(returnUrl);
			return KnownComponentsNames.WebViewer;
		}
		if (this.router.url.includes(this.IosimPlusRoute)) {
			return KnownComponentsNames.IosimPlus;
		}
		return 'unknown';
	}
		
	setOverlayState(state: OverlayState) {
		if (state.isOpen) {
			sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
		} else {
			sessionStorage.removeItem(this.STORAGE_KEY);
		}
		this.overlayStateSubject.next(state);
	}

	public closeOverlay(): void {
		this.setOverlayState({
			isOpen: false,
			appParameters: undefined,
			returnUrl: undefined
		});
		// Remove stored URL when overlay is closed
		this.removeStoredViewerUrl();
	}

	public get isOverlayOpen(): boolean {
		return this.overlayStateSubject.value.isOpen;
	}

	private processAppParameters(appParameters: AppParameters): any {
		if (!appParameters) {
			appParameters = {};
		}

		const currentUrl = this.router.url;
		const urlWithoutQuery = currentUrl.split('?')[0];
		let urlWithoutLeadingSlash = urlWithoutQuery.replace(/^\/+/, '');

		appParameters.basePath = urlWithoutLeadingSlash;
		if (!appParameters.appParameters) {
			appParameters.appParameters = {};
		}
		appParameters.appParameters = {
			...(appParameters.appParameters as any),
			basePath: urlWithoutLeadingSlash
		};

		return appParameters;
	}

	public storeViewerUrl(url: string): void {
		sessionStorage.setItem(this.VIEWER_STORAGE_KEY, url);
	}

	public getStoredViewerUrl(): string | null {
		return sessionStorage.getItem(this.VIEWER_STORAGE_KEY);
	}

	public removeStoredViewerUrl(): void {
		sessionStorage.removeItem(this.VIEWER_STORAGE_KEY);
	}
}

